import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { callApi, callApiWithAuth } from 'api/utils'
import { domainLocales } from 'domainLocales'
import { useRouterExtend } from 'hooks/useRouterExtend'
import {
  TogetherCommunity,
  TogetherPostV2,
  TogetherTopic,
} from 'interfaces/types'
import { useAmp } from 'next/amp'
import { AppContext } from 'state/appContext'

import { CommunitySection } from './communitySection'
import { getCommunityInfo, getPostList, getTopicInfo } from './helpers'
import { StyledEmbeddedCommunity } from './index.styled'
import { PostSection } from './postSection'
type EmbeddedCommunityProps = {
  communityId: number
  titleMember: string
  titleGuest: string
  descriptionMember: string
  descriptionGuest: string
  columns?: 1 | 2 | 3 | 4 | 5 | 6
  showSeparatedLine?: boolean
  isSpotlightLandingPage?: boolean
  articleId: string
  openAskDoctor?: (e: boolean) => void
}

export const EmbeddedCommunity = ({
  communityId,
  titleMember,
  titleGuest,
  descriptionGuest,
  descriptionMember,
  articleId,
  openAskDoctor,
}: EmbeddedCommunityProps) => {
  const isAmp = useAmp()
  const { locale } = useRouterExtend()
  const {
    state: { isLoggedIn },
  } = useContext(AppContext)

  const query = useMemo(
    () => ({
      communityId,
      websiteUrl: `https://${domainLocales[locale]}`,
      articleId,
    }),
    [communityId],
  )

  const fetchFunction = useMemo(() => {
    return isLoggedIn ? callApiWithAuth : callApi
  }, [])

  const [community, setCommunity] = useState<TogetherCommunity>()
  const [topics, setTopics] = useState<TogetherTopic[]>()
  const [posts, setPosts] = useState<TogetherPostV2[]>()

  const message = useMemo(() => {
    if (isLoggedIn) {
      if (community?.joined) return descriptionMember
      return descriptionGuest
    } else {
      return descriptionGuest
    }
  }, [isLoggedIn, community?.joined, descriptionMember, descriptionGuest])

  const title = useMemo(() => {
    if (isLoggedIn) {
      if (community?.joined) return titleMember || community?.name
      return titleGuest
    } else {
      return titleGuest
    }
  }, [isLoggedIn, community?.joined, titleGuest, titleMember])

  const fetchCommunity = useCallback(async () => {
    try {
      //Community
      const community = await getCommunityInfo(query, fetchFunction)
      setCommunity(community)

      //Topics
      const topics = await getTopicInfo(query, fetchFunction)
      setTopics(topics)

      //Posts
      const posts = await getPostList(query, fetchFunction)
      setPosts(posts)
    } catch (error) {
      console.error(error)
    }
  }, [query, fetchFunction])

  useEffect(() => {
    fetchCommunity()
  }, [fetchCommunity])

  if (isAmp) return null

  if (!community || !topics || !posts) return null

  return (
    <StyledEmbeddedCommunity>
      <CommunitySection
        community={community}
        topics={topics}
        title={title}
        message={message}
        openAskDoctor={openAskDoctor}
      />
      <PostSection
        isJoined={community?.joined}
        posts={posts}
        communitySlug={community?.slug}
      />
    </StyledEmbeddedCommunity>
  )
}
